import { get, set } from 'lodash';

import {
  EMPLOYMENT_TYPE_FUNNEL_TWO_OPTIONS,
  FIELD_PATHS,
  FIELD_VALUES,
  FREQUENCY,
  LAYOUT_TYPE,
} from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { Company, EmploymentEntityIndex, Income } from '@funnels/types';
import { getIndexFromId, initialFormValues } from '@funnels/utils';
import { filterCompanyOwnersOnChaneEmployment } from '@funnels/utils/src/filterCompanyOwnersOnChangeEmployment';

import { DropdownField, SecondaryAction } from '../../Fields';

const OPTIONS = [
  { label: 'Employee', value: FIELD_VALUES.EMPLOYEE },
  { label: 'Self-employed', value: FIELD_VALUES.SELF_EMPLOYED },
  { label: 'Not working', value: FIELD_VALUES.NOT_WORKING },
];

interface EmploymentTypeProps extends EmploymentEntityIndex {
  shouldHideNotWorkingOption?: boolean;
  isDisabled?: boolean;
  isOptional?: boolean;
  layoutType?: LAYOUT_TYPE;
  selected?: FIELD_VALUES;
}

export const EmploymentType = (props: EmploymentTypeProps) => {
  const { employmentIndex, shouldHideNotWorkingOption = false, layoutType, selected, ...rest } = props;
  const [, setFormValue, setFormValues, getFormValues] = useFormValues();

  const handleChange = (values: string[]) => {
    const formValues = getFormValues();
    const oldEmployment = get(formValues, FIELD_PATHS.EMPLOYMENT(employmentIndex));
    const oldEmploymentType = get(formValues, FIELD_PATHS.EMPLOYMENT(employmentIndex));
    const oldCompanyClientId = get(formValues, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex));

    if (oldEmploymentType === values[0]) return;
    const employmentType = values[0];
    const companies: Company[] = get(formValues, FIELD_PATHS.COMPANIES(), []);
    const incomes: Income[] = get(formValues, FIELD_PATHS.INCOMES(), []);
    const employmentClientId = get(formValues, FIELD_PATHS.EMPLOYMENT_CLIENT_ID(employmentIndex));
    const employmentStatus = get(formValues, FIELD_PATHS.EMPLOYMENT_TYPE(employmentIndex));
    const applicantId = get(formValues, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(employmentIndex));
    const applicants = get(formValues, FIELD_PATHS.APPLICANTS(), []);
    const applicantIndex = getIndexFromId(applicants, applicantId);
    const isBorrower =
      get(formValues, FIELD_PATHS.APPLICANT_TYPE(applicantIndex), FIELD_VALUES.BORROWER) === FIELD_VALUES.BORROWER;

    // every time employment type is changed, we need to unset the current company id in employment,
    setFormValue(FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex), undefined);

    const linkedPreviousRelatedCompanyIndex = companies.findIndex(
      (company) => company.clientId === oldCompanyClientId && !!oldCompanyClientId
    );
    const oldCompanyOwnersWithoutApplicantId = filterCompanyOwnersOnChaneEmployment(
      get(formValues, FIELD_PATHS.COMPANY(linkedPreviousRelatedCompanyIndex)),
      applicantId,
      oldEmployment
    );
    if (oldCompanyOwnersWithoutApplicantId) {
      setFormValue(FIELD_PATHS.COMPANY_OWNERS(linkedPreviousRelatedCompanyIndex), oldCompanyOwnersWithoutApplicantId);
    }

    if (employmentType === FIELD_VALUES.NOT_WORKING) {
      // if the type changed to NOT_WORKING, we need to unset certain data points and delete associated incomes
      setFormValues({
        [FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_BASIS(employmentIndex)]: undefined,
        [FIELD_PATHS.EMPLOYMENT_ROLE(employmentIndex)]: undefined,
        [FIELD_PATHS.EMPLOYMENT_ON_PROBATION(employmentIndex)]: undefined,
        [FIELD_PATHS.EMPLOYMENT_MATERNITY_LEAVE_RETURN_BASIS(employmentIndex)]: undefined,
        [FIELD_PATHS.EMPLOYMENT_MATERNITY_LEAVE_RETURN_DATE(employmentIndex)]: undefined,
        [FIELD_PATHS.INCOMES()]: incomes.filter(
          (income) => get({ incomes: [income] }, FIELD_PATHS.INCOME_EMPLOYMENT_CLIENT_ID(0)) !== employmentClientId
        ),
      });
    } else if (employmentType === FIELD_VALUES.EMPLOYEE) {
      // if the type changed to EMPLOYEE, create a new EMPLOYER company
      const company = initialFormValues.INITIAL_COMPANY();
      const companyClientId = get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0));
      setFormValue(FIELD_PATHS.COMPANY(companies.length), company);
      setFormValue(FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex), companyClientId);
      if (employmentStatus !== FIELD_VALUES.PREVIOUS && isBorrower && layoutType !== LAYOUT_TYPE.TEAM_VIEW_FUNNEL_ONE) {
        // if the employment status is not PREVIOUS, create a PAYG base salary income
        const income = initialFormValues.INITIAL_PAYG_BASE_SALARY_INCOME();
        set({ incomes: [income] }, FIELD_PATHS.INCOME_EMPLOYMENT_CLIENT_ID(0), employmentClientId);
        set({ incomes: [income] }, FIELD_PATHS.INCOME_FREQUENCY(0), FREQUENCY.YEARLY);
        setFormValue(FIELD_PATHS.INCOME(incomes.length), income);
      }
    } else if (employmentType === FIELD_VALUES.SELF_EMPLOYED) {
      // In funnel view will trigger submit by each page need to create company as well and TV starting page as well
      if (layoutType === LAYOUT_TYPE.CUSTOMER_VIEW_FUNNEL_ONE_TWO || layoutType === LAYOUT_TYPE.TEAM_VIEW_FUNNEL_ONE) {
        const company = initialFormValues.INITIAL_COMPANY(true);
        const companyClientId = get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0));
        setFormValue(FIELD_PATHS.COMPANY(companies.length), company);
        setFormValue(FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex), companyClientId);
      }

      // if the type changed to SELF_EMPLOYED, we need to remove employee incomes if any
      setFormValue(
        FIELD_PATHS.INCOMES(),
        incomes.filter(
          (income) => get({ incomes: [income] }, FIELD_PATHS.INCOME_EMPLOYMENT_CLIENT_ID(0)) !== employmentClientId
        )
      );
      // also unset probation status
      setFormValue(FIELD_PATHS.EMPLOYMENT_ON_PROBATION(employmentIndex), undefined);
    }
  };

  // funnel one/two layout
  if (layoutType === LAYOUT_TYPE.CUSTOMER_VIEW_FUNNEL_ONE_TWO) {
    return (
      <>
        {EMPLOYMENT_TYPE_FUNNEL_TWO_OPTIONS.map((option, index) => (
          <SecondaryAction
            key={index}
            variant={'primary'}
            isSelected={selected === option.value}
            name={FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(employmentIndex)}
            value={option.value}
            isFullWidth
            onClick={() => handleChange([option.value])}
          >
            {option.label}
          </SecondaryAction>
        ))}
      </>
    );
  }

  return (
    <DropdownField
      {...rest}
      name={FIELD_PATHS.EMPLOYMENT_EMPLOYMENT_TYPE(employmentIndex)}
      label="Employment type"
      options={OPTIONS.filter((option) => !shouldHideNotWorkingOption || option.value !== FIELD_VALUES.NOT_WORKING)}
      validationFailMessage="Please select the employment type"
      onChange={handleChange}
    />
  );
};
